/**
 * Camada dos dados 3D da mesa (o ambiente de dados da mesa, no original): cobre a
 * área do RPG Desk inteira, sem pegar cliques, e mostra cada rolagem que chega — de
 * qualquer aba, ficha ou chat. Fica por cima também das outras telas (Início,
 * Explorar, Configurações), para os dados rolarem sempre. Os dados saem de perto da
 * caixa do chat (quando ela está à vista) e batem nas bordas.
 *
 * Com a aba do navegador em segundo plano, o navegador não desenha, mas a rolagem acontece na
 * hora: o aviso curto toca já e, na volta, os dados aparecem no ponto em que estão (a que já
 * acabou não aparece; o resultado está no chat).
 *
 * Este módulo é leve: a física (Rapier), a cena (three.js) e o som só carregam
 * quando chega a primeira rolagem.
 *
 * A textura de quem rolou (uma imagem do Desk Cloud dele) começa a baixar quando a rolagem chega, junto
 * com o resto, e a rolagem espera por ela até `TEXTURE_WAIT_MS`. Depois disso, sai só com as cores; a
 * imagem continua baixando e fica guardada para a próxima.
 *
 * O resultado da rolagem que anima fica guardado na store até os dados pararem (docs/spec/dice-3d.md,
 * "Resultado depois da animação"): a camada o solta quando eles param, e na hora quando a rolagem não
 * anima aqui (aba em segundo plano, 3D quebrado, mesa pequena, rolagens demais).
 */
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { translate, useLanguage } from '../i18n/index.js';
import { playSound } from '../notify.js';
import { DEFAULT_SETTINGS } from '../settings.js';
import { useApp } from '../store.js';
import { matchDiceEffects, type RollResult } from '../shared.js';
import { useLoggedNav } from '../states/nav.js';
import { toast } from '../ui/Toaster.js';
import type { StagedRoll } from './DiceScene.js';
import { NO3D_REASON_KEY } from './no3dHelp.js';
import { hasWebGL, SceneBoundary, webglStatus } from './sceneGuard.js';
import { diceOn, FRESH_MS, stageDice } from './stage.js';
import { tableView } from './table.js';
import { serverNow } from '../serverClock.js';
import { loadDiceTexture, settleWithin } from './texture.js';

const DiceScene = lazy(() => import('./DiceScene.js'));

/** Rolagens animando ao mesmo tempo. */
const MAX_ACTIVE = 20;
/** Efeitos especiais ao mesmo tempo (os dados a mais aparecem sem efeito). */
const MAX_EFFECTS = 6;
/** Quanto a rolagem espera pela textura de quem rolou, desde que chegou (ms). */
const TEXTURE_WAIT_MS = 1500;

/** O resultado da rolagem aparece no chat (e vai para a ficha que rolou): os dados pararam, ou ela não anima. */
const reveal = (id: string): void => useApp.getState().revealRoll(id);

/** Sem animação (mesa escondida, sem dados com forma, 3D quebrado): o aviso curto de rolagem. */
function fallbackSound(): void {
  const { settings } = useApp.getState();
  if (settings.soundRolls) playSound('roll', settings.masterVolume);
}

/** Já avisou nesta página que não há 3D (a camada remonta a cada troca de mesa). */
let warnedNo3D = false;

/**
 * Na primeira rolagem sem WebGL da página, o aviso diz por quê, pelo motivo que o navegador deu, e
 * "Como ligar" abre Configurações → Dados, onde estão os passos (config/No3DBanner.tsx).
 */
function warnNo3D(): void {
  const status = webglStatus();
  if (warnedNo3D || status.ok) return;
  warnedNo3D = true;
  const say = (key: string) => translate(useLanguage.getState().language, key);
  toast(say(`dice.no3d.toast.${NO3D_REASON_KEY[status.reason]}`), 'info', {
    label: say(status.reason === 'policy' ? 'dice.no3d.toast.why' : 'dice.no3d.toast.fix'),
    run: () => useLoggedNav.getState().go({ kind: 'settings', page: 'dice' }),
  });
}

/** Centro do topo da caixa do chat, relativo à camada (de onde os dados saem). */
function handPoint(layer: HTMLElement): { x: number; y: number } | undefined {
  const box = layer.getBoundingClientRect();
  for (const input of layer.parentElement?.querySelectorAll<HTMLElement>('.rd-chat__input') ?? []) {
    const r = input.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return { x: r.left + r.width / 2 - box.left, y: r.top - box.top };
  }
  return undefined;
}

export function DiceLayer() {
  const rolls = useApp((s) => s.rolls);
  // a minha escolha e a regra da mesa (o mestre pode desligar a animação para todos)
  const show = useApp((s) => diceOn(s.settings, s.room));
  const lighting = useApp((s) => s.settings.dice3DLighting);
  const myTexture = useApp((s) => (s.settings.show3DDice && s.settings.diceCustom ? s.settings.diceTexture : null));
  const token = useApp((s) => s.token);
  const layerRef = useRef<HTMLDivElement>(null);
  const seen = useRef<Set<string> | null>(null);
  const [staged, setStaged] = useState<StagedRoll[]>([]);
  // a cena fica montada desde a primeira rolagem (desmontar pisca a mesa de branco)
  const [sceneOn, setSceneOn] = useState(false);
  const active = useRef(0);
  /** efeitos em cena, por rolagem (para o teto) */
  const effectsOf = useRef(new Map<string, number>());
  // sem cena 3D nesta sessão (o navegador não tem WebGL, ou a cena quebrou): não tenta de novo
  const broken = useRef(false);
  /** rolagens que chegaram com a aba em segundo plano (as mais novas, até o teto) */
  const missed = useRef<RollResult[]>([]);

  /** `late`: a rolagem entra no ponto em que está agora, sem som (o aviso curto já tocou na hora). */
  const stage = useCallback(async (roll: RollResult, late = false) => {
    const layer = layerRef.current;
    const dice = stageDice(roll.dice);
    const arrived = performance.now();
    // sem WebGL a cena nem tenta (o three.js quebraria): fica como a cena quebrada, e a mesa diz por
    // quê na primeira rolagem com dados à vista (com a aba escondida, o aviso passaria sem ninguém ver)
    if (!hasWebGL()) {
      broken.current = true;
      if (dice.length && !document.hidden) warnNo3D();
    }
    // a textura começa a baixar já, também com a aba em segundo plano: na volta, ela está pronta
    const texture = !broken.current && roll.style?.texture && dice.length ? loadDiceTexture(roll.style.texture) : null;
    // aba em segundo plano (ou janela minimizada): o navegador não desenha; o aviso curto toca já, o
    // resultado aparece (ninguém vê os dados aqui; a janela própria, se houver, está à vista) e a
    // rolagem fica para a volta, quando ela aparece no ponto em que estiver
    if (document.hidden && !broken.current && dice.length) {
      reveal(roll.id);
      missed.current = [...missed.current, roll].slice(-MAX_ACTIVE);
      // a que volta para a fila (a aba sumiu de novo) já tocou o aviso
      if (!late) fallbackSound();
      return;
    }
    // 3D quebrado, rolagem sem dados com forma, área pequena demais ou rolagens demais: só o chat mostra
    if (broken.current || !layer || !dice.length || layer.clientWidth < 120 || layer.clientHeight < 120 || active.current >= MAX_ACTIVE) {
      reveal(roll.id);
      fallbackSound();
      return;
    }
    active.current += 1;
    try {
      const view = tableView(layer.clientWidth, layer.clientHeight, handPoint(layer));
      // a cena também: a rolagem só começa a contar o tempo com tudo carregado
      const [physics, sound, scene] = await Promise.all([import('./simulation.js'), import('./diceSound.js'), import('./DiceScene.js')]);
      await physics.initPhysics();
      const { settings } = useApp.getState();
      const volume = settings.soundRolls && !late ? settings.masterVolume : 0;
      if (volume > 0) await sound.loadDiceSounds();
      const image = texture ? await settleWithin(texture, TEXTURE_WAIT_MS - (performance.now() - arrived), null) : null;
      const seed = physics.seedFromRoll(roll.id);
      const recording = physics.simulateRoll(dice, view, seed);
      // a cena quebrou enquanto esta rolagem era preparada
      if (broken.current) {
        active.current = Math.max(0, active.current - 1);
        reveal(roll.id);
        fallbackSound();
        return;
      }
      // efeitos especiais de quem rolou (sem a escolha dele, o padrão do RPG Desk), se eu mostro efeitos
      const matches = settings.showDiceEffects ? matchDiceEffects(roll.effects, roll.dice) : [];
      const inUse = [...effectsOf.current.values()].reduce((sum, n) => sum + n, 0);
      const effects = dice
        .flatMap((die, index) => {
          const match = matches.find((m) => m.die === die.source);
          return match ? [{ die: index, effect: match.effect, value: roll.dice[match.die]!.value }] : [];
        })
        .slice(0, Math.max(0, MAX_EFFECTS - inUse));
      // quanto da rolagem já passou: a que já acabou na tela dos outros não aparece mais
      const startedAgo = late ? Math.max(0, serverNow() - roll.createdAt) / 1000 : 0;
      if (startedAgo >= scene.rollSeconds(recording.frames, effects.map((e) => e.effect))) {
        active.current = Math.max(0, active.current - 1);
        reveal(roll.id);
        return;
      }
      effectsOf.current.set(roll.id, effects.length);
      setSceneOn(true);
      setStaged((list) => [
        ...list,
        {
          id: roll.id,
          recording,
          view,
          body: roll.style?.body ?? DEFAULT_SETTINGS.diceBody,
          ink: roll.style?.number ?? DEFAULT_SETTINGS.diceNumber,
          texture: image,
          textureScale: roll.style?.textureScale ?? 1,
          volume,
          stagedAt: performance.now(),
          effects,
          seed,
          startedAgo,
        },
      ]);
    } catch {
      // sem física (wasm não carregou, por exemplo): a rolagem continua no chat
      active.current -= 1;
      reveal(roll.id);
      fallbackSound();
    }
  }, []);

  // a minha textura fica baixada antes: a minha primeira rolagem não espera por ela
  useEffect(() => {
    if (myTexture && token) void loadDiceTexture(myTexture);
  }, [myTexture, token]);

  useEffect(() => {
    // o que já estava na mesa quando ela abriu não anima
    if (!seen.current) {
      seen.current = new Set(rolls.map((r) => r.id));
      // e o que estava guardado aparece: a camada de antes (de outra mesa) levou os dados embora
      for (const id of Object.keys(useApp.getState().rollingDice)) reveal(id);
      return;
    }
    for (const roll of rolls) {
      if (seen.current.has(roll.id)) continue;
      seen.current.add(roll.id);
      if (show && serverNow() - roll.createdAt < FRESH_MS) void stage(roll);
      else reveal(roll.id);
    }
  }, [rolls, show, stage]);

  // de volta à aba: as que chegaram enquanto ela estava em segundo plano aparecem no ponto em que estão
  useEffect(() => {
    const back = () => {
      if (document.hidden || !missed.current.length) return;
      const waiting = missed.current;
      missed.current = [];
      if (useApp.getState().settings.show3DDice) for (const roll of waiting) void stage(roll, true);
    };
    document.addEventListener('visibilitychange', back);
    return () => document.removeEventListener('visibilitychange', back);
  }, [stage]);

  const done = useCallback((id: string) => {
    // a que não chegou a animar (`LATE_START_MS`) solta o resultado aqui
    reveal(id);
    active.current = Math.max(0, active.current - 1);
    effectsOf.current.delete(id);
    setStaged((list) => list.filter((r) => r.id !== id));
  }, []);

  const sceneFailed = useCallback(() => {
    broken.current = true;
    // sem cena, nenhuma rolagem guardada vai parar: todas aparecem
    for (const id of Object.keys(useApp.getState().rollingDice)) reveal(id);
    active.current = 0;
    effectsOf.current.clear();
    setStaged([]);
    setSceneOn(false);
  }, []);

  return (
    <div
      ref={layerRef}
      className="rd-dice3d"
      aria-hidden="true"
      data-dice-body={staged.at(-1)?.body}
      data-dice-texture={staged.at(-1)?.texture ? String(staged.at(-1)!.textureScale) : undefined}
      data-dice-count={staged.reduce((n, r) => n + r.recording.dice.length, 0) || undefined}
      data-dice-effects={[...new Set(staged.flatMap((r) => r.effects.map((e) => e.effect)))].join(' ') || undefined}
    >
      {sceneOn ? (
        <SceneBoundary onError={sceneFailed}>
          <Suspense fallback={null}>
            <DiceScene rolls={staged} lighting={lighting} onSettled={reveal} onDone={done} />
          </Suspense>
        </SceneBoundary>
      ) : null}
    </div>
  );
}
